/**
 * La navegación del calendario: dónde estamos y cómo movernos (spec 015).
 *
 * POR QUÉ VA CENTRADA Y PEGADA A LA REJILLA
 * ─────────────────────────────────────────
 * El rótulo del periodo vivía en la cabecera de la página y las flechas para
 * cambiarlo, en la barra de filtros: **la pregunta y su respuesta en dos sitios
 * distintos**, con media pantalla en medio. Juntos y centrados, mirar dónde
 * estás y cambiarlo es el mismo gesto — y es lo que más se hace aquí.
 *
 * POR QUÉ EL SELECTOR DE FECHA DEL NAVEGADOR SE FUE
 * ────────────────────────────────────────────────
 * Era un `<input type="date">`, y tenía dos problemas a la vez:
 *
 *  · **decía la fecha dos veces.** El rótulo ya pone «jueves, 20 de agosto»; el
 *    campo repetía `20/08/2026` al lado, en otro formato.
 *  · **su calendario es diminuto** y lo dibuja el navegador, así que no se puede
 *    agrandar ni hacer que case con el resto de la pantalla.
 *
 * Ahora las flechas abrazan el rótulo —que es lo que se quiere cambiar— y el
 * rótulo **es el botón**: pulsarlo abre un mes entero, grande, dibujado aquí.
 */
import { useEffect, useRef, useState } from 'react'

import { etiquetaDia, hoyISO, mesAnterior, mesSiguiente, rotuloSemana, sumarDias } from './calendario-fechas'
import { lunesDe, mesDe, mismoMes } from './calendario-geometria'
import { rotuloReservas } from '../lib/citas-vigentes'

export type Vista = 'dia' | 'semana' | 'mes'

/** Cuánto salta cada flecha: lo que se ve es lo que se mueve. */
function saltar(dia: string, vista: Vista, sentido: 1 | -1): string {
    if (vista === 'mes') return sentido === 1 ? mesSiguiente(dia) : mesAnterior(dia)
    return sumarDias(dia, sentido * (vista === 'semana' ? 7 : 1))
}

/** Cómo se llama lo que se está viendo. */
function rotulo(dia: string, vista: Vista): string {
    if (vista === 'semana') return rotuloSemana(dia)
    if (vista === 'mes') {
        return new Date(`${dia}T12:00:00Z`).toLocaleDateString('es-ES', { month: 'long', year: 'numeric' })
    }
    return etiquetaDia(dia)
}

export function NavegacionCalendario({
    dia,
    setDia,
    vista,
    setVista,
    reservas,
    incluirPasadas = false,
}: {
    dia: string
    setDia: React.Dispatch<React.SetStateAction<string>>
    vista: Vista
    setVista: React.Dispatch<React.SetStateAction<Vista>>
    /** Spec 044 · recuento de lo pintado. `null` si la agenda no se ha leído. */
    reservas?: number | null
    incluirPasadas?: boolean
}) {
    const [abierto, setAbierto] = useState(false)

    const etiquetaAtras = vista === 'mes' ? 'Mes anterior' : vista === 'semana' ? 'Semana anterior' : 'Día anterior'
    const etiquetaAdelante =
        vista === 'mes' ? 'Mes siguiente' : vista === 'semana' ? 'Semana siguiente' : 'Día siguiente'

    return (
        <div className="flex flex-col items-center gap-2">
            {/* Las flechas ABRAZAN el rótulo: lo que se mueve está justo entre lo
                que lo mueve. */}
            <div className="flex items-center gap-1">
                <button
                    onClick={() => setDia((d) => saltar(d, vista, -1))}
                    className="min-h-11 min-w-11 rounded-md px-2.5 py-1 text-lg leading-none text-neutral-500 transition hover:bg-neutral-800 hover:text-neutral-100 md:min-h-0 md:min-w-0"
                    aria-label={etiquetaAtras}
                >
                    ←
                </button>

                <SelectorDeFecha dia={dia} abierto={abierto} setAbierto={setAbierto} alElegir={setDia}>
                    {rotulo(dia, vista)}
                </SelectorDeFecha>

                <button
                    onClick={() => setDia((d) => saltar(d, vista, 1))}
                    className="min-h-11 min-w-11 rounded-md px-2.5 py-1 text-lg leading-none text-neutral-500 transition hover:bg-neutral-800 hover:text-neutral-100 md:min-h-0 md:min-w-0"
                    aria-label={etiquetaAdelante}
                >
                    →
                </button>
            </div>

            {reservas !== null && reservas !== undefined && (
                <p
                    data-testid="reservas-vigentes"
                    className="text-sm tabular-nums text-sky-300"
                    title={
                        incluirPasadas
                            ? 'Citas de esta vista, hayan empezado o no'
                            : 'Citas de esta vista que aún no han empezado'
                    }
                >
                    {rotuloReservas(reservas)}
                </p>
            )}

            <div className="flex flex-wrap items-center justify-center gap-2">
                <div className="flex items-center rounded-md border border-neutral-800">
                    {(
                        [
                            ['dia', 'Día'],
                            ['semana', 'Semana'],
                            ['mes', 'Mes'],
                        ] as const
                    ).map(([v, et]) => (
                        <button
                            key={v}
                            onClick={() => {
                                setVista(v)
                                // El lunes es el primer día de la semana, así que en
                                // vista de semana lo que se enseña arriba es SU día.
                                // Sin esto, elegir el miércoles y pasar a semana
                                // dejaba dos fechas para la misma cosa.
                                if (v === 'semana') setDia((d) => lunesDe(d))
                            }}
                            aria-pressed={vista === v}
                            className={
                                'min-h-11 px-3 py-1 text-sm transition md:min-h-0 ' +
                                (vista === v
                                    ? 'bg-neutral-800 text-neutral-100'
                                    : 'text-neutral-500 hover:text-neutral-300')
                            }
                        >
                            {et}
                        </button>
                    ))}
                </div>

                <button
                    onClick={() => setDia(hoyISO())}
                    className="min-h-11 rounded-md border border-neutral-800 px-3 py-1 text-sm text-neutral-400 hover:text-neutral-100 md:min-h-0"
                >
                    Hoy
                </button>
            </div>
        </div>
    )
}

const CABECERA = ['L', 'M', 'X', 'J', 'V', 'S', 'D']

/**
 * El rótulo es el botón, y al pulsarlo sale un mes entero.
 *
 * Se dibuja aquí en vez de dejárselo al navegador porque el suyo es diminuto, no
 * se puede agrandar y no se parece al resto de la pantalla. Las celdas miden lo
 * que hay que medir para pulsarlas **con el pulgar**, que es donde fallaba.
 *
 * EL MES DEL SELECTOR ES SUYO, NO EL DE LA PANTALLA
 * ────────────────────────────────────────────────
 * Se puede pasear por diciembre sin que el calendario de detrás se mueva: solo
 * cambia al ELEGIR un día. Si fueran el mismo, cada paso por el selector
 * dispararía una petición a la agenda por un mes que quizá no interesa.
 */
function SelectorDeFecha({
    dia,
    abierto,
    setAbierto,
    alElegir,
    children,
}: {
    dia: string
    abierto: boolean
    setAbierto: (v: boolean) => void
    alElegir: (dia: string) => void
    children: React.ReactNode
}) {
    const caja = useRef<HTMLDivElement>(null)
    const [mesVisible, setMesVisible] = useState(dia)

    // Al abrirlo se sitúa en el día que se está viendo. Sin esto, abrirlo tras
    // pasear por otro mes lo dejaría donde se quedó la vez anterior.
    useEffect(() => {
        if (abierto) setMesVisible(dia)
    }, [abierto, dia])

    useEffect(() => {
        if (!abierto) return
        const fuera = (e: MouseEvent) => {
            if (caja.current && !caja.current.contains(e.target as Node)) setAbierto(false)
        }
        const esc = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setAbierto(false)
        }
        document.addEventListener('mousedown', fuera)
        document.addEventListener('keydown', esc)
        return () => {
            document.removeEventListener('mousedown', fuera)
            document.removeEventListener('keydown', esc)
        }
    }, [abierto, setAbierto])

    const elegir = (d: string) => {
        alElegir(d)
        setAbierto(false)
    }

    const hoy = hoyISO()

    return (
        <div ref={caja} className="relative">
            <button
                onClick={() => setAbierto(!abierto)}
                aria-expanded={abierto}
                className="min-h-11 rounded-md px-3 py-1 text-base font-medium capitalize text-neutral-100 transition hover:bg-neutral-800 md:min-h-0"
                title="Elegir fecha"
            >
                {children}
            </button>

            {abierto && (
                <div className="absolute left-1/2 top-full z-30 mt-2 w-[20rem] max-w-[calc(100vw-2rem)] -translate-x-1/2 rounded-lg border border-neutral-700 bg-neutral-900 p-3 shadow-xl">
                    <div className="mb-2 flex items-center justify-between">
                        <button
                            onClick={() => setMesVisible((m) => mesAnterior(m))}
                            className="rounded-md px-3 py-1.5 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
                            aria-label="Mes anterior"
                        >
                            ←
                        </button>
                        <span className="text-sm font-medium capitalize text-neutral-200">
                            {new Date(`${mesVisible}T12:00:00Z`).toLocaleDateString('es-ES', {
                                month: 'long',
                                year: 'numeric',
                            })}
                        </span>
                        <button
                            onClick={() => setMesVisible((m) => mesSiguiente(m))}
                            className="rounded-md px-3 py-1.5 text-neutral-400 hover:bg-neutral-800 hover:text-neutral-100"
                            aria-label="Mes siguiente"
                        >
                            →
                        </button>
                    </div>

                    <div className="grid grid-cols-7 gap-0.5">
                        {CABECERA.map((d, i) => (
                            <div key={i} className="py-1 text-center text-xs text-neutral-500">
                                {d}
                            </div>
                        ))}

                        {mesDe(mesVisible).map((d) => {
                            const deEsteMes = mismoMes(d, mesVisible)
                            const elegido = d === dia
                            const esHoy = d === hoy
                            return (
                                <button
                                    key={d}
                                    onClick={() => elegir(d)}
                                    aria-current={elegido ? 'date' : undefined}
                                    className={
                                        'h-10 rounded-md text-sm tabular-nums transition ' +
                                        (elegido
                                            ? 'bg-sky-500 font-medium text-neutral-950'
                                            : esHoy
                                              ? 'bg-neutral-800 font-medium text-sky-300'
                                              : deEsteMes
                                                ? 'text-neutral-200 hover:bg-neutral-800'
                                                : 'text-neutral-600 hover:bg-neutral-800/60')
                                    }
                                >
                                    {Number(d.slice(8, 10))}
                                </button>
                            )
                        })}
                    </div>

                    <button
                        onClick={() => elegir(hoy)}
                        className="mt-2 w-full rounded-md border border-neutral-700 py-2 text-sm text-neutral-300 hover:bg-neutral-800"
                    >
                        Ir a hoy
                    </button>
                </div>
            )}
        </div>
    )
}
